import React, { useEffect, useState, useRef } from 'react';
import { Upload, Trash2, Copy, Check, Image } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../api/adminApi';

const MediaPage = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const fileRef = useRef(null);

  const fetchFiles = () => {
    setLoading(true);
    adminApi.get('/admin/media').then(r => setFiles(r.data.files || [])).catch(() => toast.error('Failed to load media')).finally(() => setLoading(false));
  };

  useEffect(() => { fetchFiles(); }, []);

  const handleUpload = async (e) => {
    const uploadFiles = Array.from(e.target.files || []);
    if (!uploadFiles.length) return;
    setUploading(true);
    let success = 0;
    for (const file of uploadFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await adminApi.post('/admin/media/upload', formData);
        success++;
      } catch (err) {
        toast.error(`Failed: ${file.name} — ${err.response?.data?.detail || 'Error'}`);
      }
    }
    if (success > 0) toast.success(`${success} file${success > 1 ? 's' : ''} uploaded`);
    setUploading(false);
    fetchFiles();
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (id, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await adminApi.delete(`/admin/media/${id}`);
      toast.success('Deleted');
      fetchFiles();
    } catch { toast.error('Delete failed'); }
  };

  const copyUrl = (url) => {
    const fullUrl = window.location.origin + url;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(url);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Media Library</h1>
          <p className="text-white/40 text-sm mt-0.5">{files.length} files uploaded</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black font-semibold text-sm rounded-sm hover:bg-[#C5A028] transition-colors disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />{uploading ? 'Uploading...' : 'Upload Images'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
        </div>
      </div>

      <div className="bg-[rgba(212,175,55,0.05)] border border-[rgba(212,175,55,0.2)] rounded-sm px-4 py-3 text-white/50 text-xs">
        Supported formats: JPEG, PNG, WebP, GIF · Max 5MB per file · Click an image's copy button to get the URL for use in editors
      </div>

      {loading && <p className="text-white/40 text-sm text-center py-12">Loading...</p>}

      {!loading && files.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-white/30">
          <Image className="w-12 h-12 mb-3" />
          <p className="text-sm">No images yet. Upload some above.</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {files.map(file => (
          <div key={file.id} className="group bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm overflow-hidden hover:border-[rgba(212,175,55,0.3)] transition-colors">
            <div className="aspect-square bg-[#0A0A0A] relative">
              <img
                src={file.url}
                alt={file.original_name}
                className="w-full h-full object-cover"
                onError={e => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button onClick={() => copyUrl(file.url)} className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-[#D4AF37] hover:text-black text-white rounded-sm transition-colors" title="Copy URL">
                  {copied === file.url ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                <button onClick={() => handleDelete(file.id, file.original_name)} className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-red-500 text-white rounded-sm transition-colors" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="px-2 py-1.5">
              <p className="text-white/60 text-[10px] truncate">{file.original_name}</p>
              <p className="text-white/30 text-[10px]">{formatSize(file.size_bytes)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MediaPage;
